import { useLocation, useNavigate } from 'react-router-dom';
import AlertComponent from 'components/alertComponent';
import ButtonComponent from 'components/button/buttonComponent';
import { DASHBOARD, MY_APPOINTMENTS } from 'utilities/routes';
import { useGetAppointmentByCodexAppointmentIdQuery } from 'graphql/generated/remote-schema-hasura';
import Loader from 'components/loaderComponent';
import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { parseDuration } from '../../helpers/groupServiceType';
import { formatDateTime } from 'utilities/functions';
import { FULL_APPOINTMENT_DATE_FORMAT_WITH_FULL_MONTH } from 'utilities/dateHelper';

interface AppointmentCompleteProps {
  locale: Record<string, Record<string, string | Record<string, string>>>;
}

export const AppointmentComplete: React.FC<AppointmentCompleteProps> = ({
  locale,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { appointmentId } = location.state;

  const { data, loading } = useGetAppointmentByCodexAppointmentIdQuery({
    skip: !appointmentId,
    variables: { appointmentCodexId: appointmentId },
  });

  const appointment = useMemo(
    () => data?.getFHIRAppointmentByCodexId.appointment,
    [data],
  );

  const duration = useMemo(() => {
    const durationObj = parseDuration(appointment?.appointmentType as string);
    if (!durationObj) {
      return null;
    }
    return durationObj;
  }, [appointment?.appointmentType]);

  const handleClose = () => {
    navigate(DASHBOARD);
  };

  const [, forceUpdate] = useState(0);

  useEffect(() => {
    if (!appointment?.end) return;
    const msUntilEnd = moment(appointment.end).diff(moment()) + 1000;
    if (msUntilEnd <= 0) return;

    const timer = setTimeout(() => {
      forceUpdate((n) => n + 1);
    }, msUntilEnd);

    return () => clearTimeout(timer);
  }, [appointment]);

  if (loading) {
    return <Loader />;
  }

  if (moment().isSameOrBefore(appointment?.end)) {
    return (
      <>
        <div className="w-full h-full flex flex-col items-center justify-center gap-30">
          <div className="flex w-full">
            <AlertComponent type="info" text="Call disconnected" />
          </div>
          <div className="flex items-center justify-center flex-col w-full p-5 desktop:py-20 desktop:px-[22rem] gap-8 bg-white rounded-lg">
            <div className="text-h1 font-medium text-neutral-800 text-center">
              You've left the call, but your appointment is still in progress
            </div>
            <div className="text-h6 font-medium text-center text-med-gray-3 -mt-6 px-32">
              Your appointment with{' '}
              <span className="font-semibold text-neutral-800">
                {appointment?.providerFirstName} {appointment?.providerLastName}
              </span>{' '}
              is still ongoing. You can rejoin the call to continue your
              consultation.
            </div>

            <div className="desktop:p-8 p-5 rounded-lg flex items-center justify-center flex-col w-full max-w-xl bg-neutral-100">
              <p className="text-center font-medium text-neutral-800">
                {duration?.live} minutes{' '}
                <span className="lowercase">
                  {appointment?.appointmentType?.split('_')[0]}
                </span>{' '}
                with {appointment?.providerFirstName}{' '}
                {appointment?.providerLastName} on
              </p>
              <p className="font-semibold text-charcoal-gray text-h3 mt-2.5 mb-5 text-center">
                {formatDateTime(
                  appointment?.start as string,
                  FULL_APPOINTMENT_DATE_FORMAT_WITH_FULL_MONTH,
                )}
              </p>

              <ButtonComponent
                onClick={() => navigate(-1)}
                testID={'go-home'}
                fullWidthClassName="w-auto"
                className="!text-sm !px-5 !py-3 !rounded-[5px]"
              >
                Rejoin call
              </ButtonComponent>
            </div>

            <ButtonComponent
              onClick={() => navigate(MY_APPOINTMENTS)}
              paddingX="px-10"
              type="outlined"
              testID={'close'}
            >
              Go to appointments
            </ButtonComponent>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="w-full h-full flex flex-col items-center justify-center gap-[60px]">
        <div className="flex flex-col items-center gap-2">
          <div className="flex w-auto">
            <AlertComponent
              type="positive"
              text={locale?.complete?.alert as string}
            />
          </div>
          <div className="text-h1 font-medium text-neutral-800 mb-9 text-center">
            {locale?.complete?.title as string}
          </div>
          <div className="text-h6 font-normal text-center text-med-gray-3 w-[690px] mb-[30px]">
            {locale?.complete?.description as string}
          </div>
          <div className="mt-4">
            <ButtonComponent
              onClick={handleClose}
              paddingX="px-10"
              testID={'close'}
            >
              Go to dashboard
            </ButtonComponent>
          </div>
        </div>
      </div>
    </>
  );
};
